import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Close, CardGiftcard, CheckCircle } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { StandaloneExam } from '../../types';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

interface FreeExamEnrollModalProps {
  open: boolean;
  onClose: () => void;
  exam: StandaloneExam;
  /** Called after enrollment succeeds so the parent can refetch purchases/access state */
  onEnrolled?: () => void;
}

interface FormValues {
  email: string;
}

const FreeExamEnrollModal: React.FC<FreeExamEnrollModalProps> = ({ open, onClose, exam, onEnrolled }) => {
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({ defaultValues: { email: '' } });

  useEffect(() => {
    if (open) {
      reset({ email: '' });
      setError('');
      setShowSuccess(false);
    }
  }, [open, reset]);

  const submitEnrollment = async (email?: string) => {
    try {
      setLoading(true);
      setError('');
      await apiService.enrollFreeStandaloneExam({ standalone_exam_id: exam.id, email });
      onEnrolled?.();
      setShowSuccess(true);
    } catch (err: any) {
      const message = err.response?.data?.detail || 'Enrollment failed';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (data: FormValues) => submitEnrollment(data.email);
  const onInstantEnroll = () => submitEnrollment(undefined);

  const handleClose = () => {
    reset();
    setError('');
    setShowSuccess(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      {showSuccess ? (
        <DialogContent sx={{ textAlign: 'center', py: 5, px: 4 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              boxShadow: '0 8px 30px rgba(16, 185, 129, 0.4)',
            }}
          >
            <CheckCircle sx={{ fontSize: '3rem', color: 'white' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            {isAuthenticated ? "You're In!" : 'Almost There!'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
            {isAuthenticated
              ? `You now have free access to "${exam.title}". You can take it right away.`
              : `We've emailed you an access code for "${exam.title}". Use it on the Create Account page to set up your account and take the exam immediately — no waiting for review.`}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleClose}
            sx={{
              background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #059669 0%, #16a34a 100%)' },
            }}
          >
            Got It!
          </Button>
        </DialogContent>
      ) : (
        <>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CardGiftcard sx={{ color: '#10b981', fontSize: 24 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Get Free Access to {exam.title}
              </Typography>
            </Box>
            <IconButton onClick={handleClose} size="small">
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ px: 3, py: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {isAuthenticated ? (
              <Typography variant="body1" color="text.secondary">
                You're logged in as <strong>{user?.email}</strong>. This exam is free — click below
                for instant access, no payment required.
              </Typography>
            ) : (
              <form id="free-exam-enroll-form" onSubmit={handleSubmit(onSubmit)}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  This exam is free. Enter your email and we'll send you an access code to create
                  your account and take the exam instantly — no payment, no waiting.
                </Typography>
                <TextField
                  fullWidth
                  required
                  label="Email Address"
                  type="email"
                  autoFocus
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              </form>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={handleClose}
              disabled={loading}
              variant="outlined"
              sx={{
                borderColor: '#10b981',
                color: '#10b981',
                '&:hover': { borderColor: '#059669', backgroundColor: 'rgba(16, 185, 129, 0.1)' },
              }}
            >
              Cancel
            </Button>
            <Button
              type={isAuthenticated ? 'button' : 'submit'}
              form={isAuthenticated ? undefined : 'free-exam-enroll-form'}
              onClick={isAuthenticated ? onInstantEnroll : undefined}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
              sx={{
                backgroundColor: '#10b981',
                '&:hover': { backgroundColor: '#059669' },
              }}
            >
              {loading ? 'Enrolling...' : 'Get Free Access'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default FreeExamEnrollModal;
